import DownloadIcon from "@mui/icons-material/Download"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Skeleton,
    SortDirection,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { visuallyHidden } from "@mui/utils"
import dayjs from "dayjs"
import { MouseEvent, ReactElement, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import DataLoadError from "../../components/fallback/dataLoadError"
import DNOFilterPanelFrame from "../../components/fitlerPanel/dnoFilterPanelFrame"
import MonthBucketSkeleton from "../../components/loading/skeletons/monthBucketSkeleton"
import StatusChip from "../../components/pills/status/statusChip"
import AppPaper from "../../components/surfaces/appPaper"
import MonthHeader from "../../components/surfaces/monthHeader"
import AppTableCell from "../../components/tables/appTableCell"
import AppTableContentRow from "../../components/tables/appTableContentRow"
import {
    ASC,
    DESC,
    ORDER_KW,
    SLIDE_IN_TIMEOUT,
    STATUS,
} from "../../constants/generalConstants"
import { TT_HELP_DNO_SOLAR_NOTIFICATIONS } from "../../constants/tooltips"
import { useApplicationDeriveFilters } from "../../hooks/useApplicationsDeriveFiltersFromUrl"
import useDownloadJson from "../../hooks/useDownloadJson"
import { useGetState } from "../../hooks/useGetState"
import { getShowDnoApplicationFilters } from "../../redux/preferencesSlice"
import useGetDnoSolarNotifications from "../../services/middleware/hooks/dnoInterface/protected/useGetDnoSolarNotifications"
import usePostDnoSolExportMpan from "../../services/middleware/hooks/dnoInterface/protected/usePostDnoSolExportMpan"
import { IDnoSolarNotification } from "../../services/middleware/mappers/dnoInterface/dnoSolarNotificationsMapper"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

const CURRENT_STATUS = "Current"
const HISTORIC_STATUS = "Historic"
const TABLE_HEADINGS = [
    { title: "Address", width: "20%" },
    { title: "Postcode", width: "9%" },
    { title: "Name", width: "15%" },
    { title: "Email", width: "16%" },
    { title: "Device", width: "12%" },
    { title: "Capacity (kW)", width: "9%" },
    { title: "Export MPAN", width: "11%" },
    { title: "Submitted date", width: "10%" },
    { title: "Status", width: "8%" },
]

const formatRegisteredCapacity = (
    capacity: number | null | undefined
): string =>
    typeof capacity === "number" && Number.isFinite(capacity)
        ? `${capacity}`
        : "Not provided"
const DATA_CELL_SX = { typography: "body1", overflow: "hidden" }

const TruncatedCellText = ({
    text,
}: {
    text: string | null | undefined
}): ReactElement => (
    <Typography
        variant="body1"
        noWrap
        title={text || undefined}
        sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
    >
        {text}
    </Typography>
)

type NotificationMonthBucket = {
    key: string
    label: string
    data: IDnoSolarNotification[]
}

const SolarNotificationsTable = ({
    notifications,
    isLoading,
    orderDirection,
    onSubmittedDateSort,
    onSelectNotification,
}: {
    notifications: IDnoSolarNotification[]
    isLoading: boolean
    orderDirection: string
    onSubmittedDateSort: (event: MouseEvent<unknown>) => void
    onSelectNotification: (notification: IDnoSolarNotification) => void
}): ReactElement => (
    <TableContainer>
        <Table size="medium" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
                <TableRow>
                    {TABLE_HEADINGS.map((heading) => (
                        <AppTableCell
                            key={heading.title}
                            sortDirection={
                                heading.title === "Submitted date"
                                    ? (orderDirection as SortDirection)
                                    : false
                            }
                            sx={{
                                width: heading.width,
                                cursor:
                                    heading.title === "Submitted date"
                                        ? "pointer"
                                        : null,
                            }}
                        >
                            <TableSortLabel
                                active={heading.title === "Submitted date"}
                                direction={
                                    orderDirection as "asc" | "desc" | undefined
                                }
                                onClick={onSubmittedDateSort}
                                disabled={heading.title !== "Submitted date"}
                                sx={{
                                    "& .MuiTableSortLabel-icon": {
                                        opacity: 0,
                                    },
                                    "&:hover .MuiTableSortLabel-icon, &.Mui-active .MuiTableSortLabel-icon":
                                        {
                                            opacity: 1,
                                        },
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                    color="primary"
                                >
                                    {heading.title}
                                </Typography>
                                {heading.title === "Submitted date" ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {orderDirection === DESC
                                            ? "sorted descending"
                                            : "sorted ascending"}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </AppTableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {isLoading
                    ? Array.from(Array(2).keys()).map((value) => (
                          <AppTableContentRow key={value}>
                              {Array.from(Array(9).keys()).map((cell) => (
                                  <AppTableCell key={cell}>
                                      <Skeleton />
                                  </AppTableCell>
                              ))}
                          </AppTableContentRow>
                      ))
                    : notifications.map((notification) => (
                          <AppTableContentRow
                              key={notification.notificationId}
                              hover
                              onClick={() => onSelectNotification(notification)}
                              sx={{ cursor: "pointer" }}
                          >
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.address}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.postCode}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText text={notification.name} />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.email}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.device}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={formatRegisteredCapacity(
                                          notification.registeredCapacity
                                      )}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.exportMpan}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <TruncatedCellText
                                      text={notification.createdDate}
                                  />
                              </AppTableCell>
                              <AppTableCell sx={DATA_CELL_SX}>
                                  <StatusChip status={notification.status} />
                              </AppTableCell>
                          </AppTableContentRow>
                      ))}
            </TableBody>
        </Table>
    </TableContainer>
)

/**
 * Lists all plug-in solar notifications with monthly batched loading.
 */
const ListNotifications = (): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [selectedNotification, setSelectedNotification] =
        useState<IDnoSolarNotification | null>(null)
    const [exportMpanValue, setExportMpanValue] = useState("")
    const { isSending: isSubmittingExportMpan, postDnoSolExportMpan } =
        usePostDnoSolExportMpan()
    const {
        data,
        isError,
        isLoading,
        isValidating,
        downloadData,
        isFinalPage,
        page,
        setPage,
        mutate: mutateNotifications,
    } = useGetDnoSolarNotifications(searchParams)
    const { downloadJson } = useDownloadJson()
    const showFilters = useGetState(getShowDnoApplicationFilters)
    const { applicationId, setFilterFromTextfield, status } =
        useApplicationDeriveFilters(searchParams, setSearchParams, true)
    const theme = useTheme()
    const statusFilter = status || null
    const orderDirection = searchParams.get(ORDER_KW) || DESC
    const monthBuckets = useMemo<NotificationMonthBucket[]>(() => {
        const grouped = new Map<string, NotificationMonthBucket>()
        ;(data || []).forEach((notification) => {
            const submittedDate = dayjs(notification.createdDate)
            const key = submittedDate.isValid()
                ? submittedDate.format("YYYY-MM")
                : "unknown"
            const label = submittedDate.isValid()
                ? submittedDate.format("MMMM YYYY")
                : "Unknown"
            const bucket = grouped.get(key) || { key, label, data: [] }
            bucket.data.push(notification)
            grouped.set(key, bucket)
        })
        return Array.from(grouped.values())
    }, [data])

    const handleSubmittedDateSort = (): void => {
        const isAsc = orderDirection === ASC
        searchParams.set(ORDER_KW, isAsc ? DESC : ASC)
        setSearchParams(new URLSearchParams(searchParams.toString()))
    }

    const handleSelectNotification = (
        notification: IDnoSolarNotification
    ): void => {
        setSelectedNotification(notification)
        setExportMpanValue(notification.exportMpan || "")
    }

    const getNotificationExportMpanError = (value: string): string | null => {
        const trimmedValue = value.trim()

        if (!trimmedValue) {
            return null
        }

        if (!/^\d{13}$/.test(trimmedValue)) {
            return "Export MPAN must be 13 numerical values"
        }

        return null
    }

    const exportMpanError = getNotificationExportMpanError(exportMpanValue)
    const isHistoricNotification =
        selectedNotification?.status === HISTORIC_STATUS

    const handleExportMpanSubmit = async (): Promise<void> => {
        if (!selectedNotification) return

        if (selectedNotification.status === HISTORIC_STATUS) return

        const trimmedValue = exportMpanValue.trim()
        if (!trimmedValue || exportMpanError) return

        await postDnoSolExportMpan(
            selectedNotification.notificationId,
            trimmedValue,
            () => {
                setSelectedNotification(null)
                mutateNotifications()
            }
        )
    }

    return (
        <Grid container spacing={GRID_SPACING}>
            <DNOFilterPanelFrame
                applicationId={applicationId}
                isValidating={isValidating}
                all
                heading="Plug-in Solar"
                applications="Notifications"
                tooltip={TT_HELP_DNO_SOLAR_NOTIFICATIONS}
                colour={theme.palette.success.main}
                isDnoMode
                clearLocalAddress={() => undefined}
                clearLocalPostcode={() => undefined}
                clearLocalInstallerEmail={() => undefined}
                clearLocalGenCapacity={() => undefined}
                clearLocalDemCapacity={() => undefined}
            />
            <Grid xs={12}>
                <AppPaper>
                    <Collapse in={showFilters} timeout={SLIDE_IN_TIMEOUT}>
                        <Grid
                            container
                            spacing={GRID_SPACING}
                            marginBottom={4}
                            marginTop={1}
                            columns={12}
                            display="flex"
                            alignItems="center"
                        >
                            <Grid xl={3} md={6} sm={12} xs={12}>
                                <ToggleButtonGroup
                                    color="primary"
                                    fullWidth
                                    value={statusFilter}
                                    exclusive
                                    onChange={(_e, v) => {
                                        if (v) setFilterFromTextfield(v, STATUS)
                                    }}
                                >
                                    <ToggleButton
                                        value={CURRENT_STATUS}
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        Current
                                    </ToggleButton>
                                    <ToggleButton
                                        value={HISTORIC_STATUS}
                                        sx={{
                                            borderRadius:
                                                BUTTON_RADIUS_MULTIPLIER,
                                            textTransform: "none",
                                        }}
                                    >
                                        Historic
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid
                                xl={6}
                                md={6}
                                sm={12}
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    pr: 2,
                                }}
                            >
                                <AppButton
                                    variant="contained"
                                    endIcon={<DownloadIcon />}
                                    disabled={
                                        isLoading || isError || !downloadData
                                    }
                                    onClick={() =>
                                        downloadData &&
                                        downloadJson(downloadData, "results")
                                    }
                                >
                                    Download
                                </AppButton>
                            </Grid>
                        </Grid>
                    </Collapse>

                    {isError ? (
                        <DataLoadError />
                    ) : (
                        <Stack spacing={GRID_SPACING}>
                            {monthBuckets.map((bucket) => (
                                <Collapse
                                    key={bucket.key}
                                    in
                                    appear
                                    timeout={300}
                                >
                                    <Box>
                                        <MonthHeader
                                            label={bucket.label}
                                            recordCount={bucket.data.length}
                                            isLoading={isLoading}
                                            monthKey={bucket.key}
                                            recordLabel="notification"
                                        />
                                        <SolarNotificationsTable
                                            notifications={bucket.data}
                                            isLoading={isLoading}
                                            orderDirection={orderDirection}
                                            onSubmittedDateSort={
                                                handleSubmittedDateSort
                                            }
                                            onSelectNotification={
                                                handleSelectNotification
                                            }
                                        />
                                    </Box>
                                </Collapse>
                            ))}
                            {isLoading && <MonthBucketSkeleton />}
                            {data && (
                                <Stack
                                    alignItems="center"
                                    justifyContent="center"
                                    direction="row"
                                    width="100%"
                                >
                                    {!isFinalPage && (
                                        <AppLoadingButton
                                            color="secondary"
                                            endIcon={<MoreHorizIcon />}
                                            loading={isValidating}
                                            loadingPosition="end"
                                            onClick={() => setPage(page + 1)}
                                            sx={{
                                                width: "20%",
                                                mt: "6px",
                                            }}
                                        >
                                            See More
                                        </AppLoadingButton>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </AppPaper>
            </Grid>
            <Dialog
                open={Boolean(selectedNotification)}
                onClose={() => setSelectedNotification(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>DNO - Notification Details</DialogTitle>
                <DialogContent dividers>
                    {selectedNotification && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Address
                                </Typography>
                                <Typography>
                                    {selectedNotification.address ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Postcode
                                </Typography>
                                <Typography>
                                    {selectedNotification.postCode ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Name
                                </Typography>
                                <Typography>
                                    {selectedNotification.name ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Email
                                </Typography>
                                <Typography>
                                    {selectedNotification.email ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Device
                                </Typography>
                                <Typography>
                                    {selectedNotification.device ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Registered capacity
                                </Typography>
                                <Typography>
                                    {formatRegisteredCapacity(
                                        selectedNotification.registeredCapacity
                                    )}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Submitted date
                                </Typography>
                                <Typography>
                                    {selectedNotification.createdDate ||
                                        "Not provided"}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Status
                                </Typography>
                                <Box mt={0.5}>
                                    <StatusChip
                                        status={selectedNotification.status}
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Export MPAN
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={exportMpanValue}
                                    onChange={(event) =>
                                        !isHistoricNotification &&
                                        setExportMpanValue(event.target.value)
                                    }
                                    placeholder="Enter Export MPAN"
                                    disabled={isHistoricNotification}
                                    error={
                                        Boolean(exportMpanError) &&
                                        !isHistoricNotification
                                    }
                                    helperText={
                                        isHistoricNotification
                                            ? "Historic notifications cannot submit or change an Export MPAN."
                                            : exportMpanError || " "
                                    }
                                    sx={{ mt: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleExportMpanSubmit}
                                    disabled={
                                        !selectedNotification ||
                                        !exportMpanValue.trim() ||
                                        Boolean(exportMpanError) ||
                                        isSubmittingExportMpan ||
                                        isHistoricNotification
                                    }
                                    sx={{ mt: 1 }}
                                >
                                    {isSubmittingExportMpan
                                        ? "Submitting..."
                                        : "Submit"}
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => setSelectedNotification(null)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

export default ListNotifications
